import { convertLocalDateRangeToUTC } from "../DateUtils/dateTimezoneUtils";

type UIFilter = {
  filterField: string;
  filterValue: any;
};

/**
 * List of date fields that should be converted from local to UTC
 * These are quotation-related date fields that need timezone conversion
 */
const DATE_FIELDS = [
  "quotation_date",
  "valid_until_date",
  "inspection_date",
  "installation_date",
  "next_follow_up_date",
  "next_followup_date", // Backend uses this format (without underscore)
  "last_follow_up_date",
  "follow_up_date",
  "created_at",
  "updated_at",
  "sales_order_date",
  "srv_date",
  "enquiry_date",
  "service_completion_date",
  "customer_po_date", // Customer PO date for sales orders
  "pr_date",
  "approved_date",
  "po_date",
  "eta_date",
  "challan_date",
  "inward_date",
  "order_date",
];

/**
 * Checks if a field is a date field that needs UTC conversion
 */
const isDateField = (fieldName: string): boolean => {
  return DATE_FIELDS.some((dateField) => 
    fieldName.toLowerCase().includes(dateField.toLowerCase())
  );
};

export function FilterBuilder(uiFilters: UIFilter[] = []) {
  const filters: any = {};
  const range: any = {};

  // Debug: Log input filters
  console.log("FilterBuilder Input:", JSON.stringify(uiFilters, null, 2));

  // 🔹 Helper: set nested value safely
  const setNestedValue = (obj: any, path: string[], value: any) => {
    let current = obj;
    for (let i = 0; i < path.length; i++) {
      const key = path[i];
      if (i === path.length - 1) {
        current[key] = value;
      } else {
        current[key] = current[key] || {};
        current = current[key];
      }
    }
  };

  for (const item of uiFilters) {
    const { filterField, filterValue } = item;

    if (
      filterValue === undefined ||
      filterValue === null ||
      filterValue === ""
    ) {
      continue;
    }

    // 🔹 RANGE FILTER (date / number)
    if (
      typeof filterValue === "object" &&
      (filterValue.from_date !== undefined ||
        filterValue.to_date !== undefined)
    ) {
      // Convert local date ranges to UTC for date fields
      if (isDateField(filterField)) {
        const utcRange = convertLocalDateRangeToUTC(
          filterValue.from_date,
          filterValue.to_date
        );
        range[filterField] = utcRange;
      } else {
        // For non-date fields, keep as is
        range[filterField] = {
          ...(filterValue.from_date && { from: filterValue.from_date }),
          ...(filterValue.to_date && { to: filterValue.to_date }),
        };
      }
      continue;
    }

    // 🔹 NESTED FIELD (supports "." and "__")
    if (filterField.includes(".") || filterField.includes("__")) {
      const path = filterField
        .replace(/\./g, "__")
        .split("__");

      setNestedValue(filters, path, filterValue);
      continue;
    }

    // 🔹 SIMPLE FIELD
    filters[filterField] = filterValue;
  }

  // 🔹 Attach range filters
  if (Object.keys(range).length > 0) {
    filters.range = range;
  }

  // Debug: Log output filters
  console.log("FilterBuilder Output:", JSON.stringify(filters, null, 2));

  return filters;
}
